import { useCallback, useMemo, useState } from "react";
import Deactivate from "components/blocks/modal/deactivate";
import useStatusMutation from "hook/useStatusMutation";
import {
  deactiveProfile,
  deletableUser,
  deleteUser,
  getUserDetail,
} from "api/userAPI";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import {
  AccountInfo,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "react-query";
import { baseQueryInfo } from "utils/queryUtil";
import { getSwitchAccountList } from "api/accountAPI";
import { useMenu } from "hook/useMenu";

export default function DeactivateUser({
  email,
  isProfile,
  onSuccessDelete,
  userId,
}: {
  email: string;
  isProfile?: boolean;
  onSuccessDelete?: () => void;
  userId: string;
}) {
  const { user, logout } = useAuth();
  const { navigateSwitchAccount } = useMenu({});
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { handleErrorNotify, notifySuccess, lang } = useApiError();
  const getAccountSwitch = useQuery(
    ["accountSwitch", userId, selectedAccount.accountId],
    () =>
      getSwitchAccountList({
        payload: {
          ...baseQueryInfo,
          pageLimit: 10000,
        },
      }),

    {
      retry: 0,
      cacheTime: 0,
      enabled: user.userId === userId,
      refetchOnWindowFocus: true,
      onError: (e: any) => {
        console.log(e, "e");
      },
    }
  );
  const deletable = useQuery(
    ["userDeletable", userId, selectedAccount.accountId],
    () =>
      deletableUser({
        accountId: selectedAccount.accountId,
        userId: userId as string,
      }),
    {
      retry: 0,
      enabled: userId !== undefined && !isProfile,
      refetchOnWindowFocus: false,
    }
  );
  const switchAccount = useMemo(() => {
    return getAccountSwitch?.data?.result?.filter(
      (account: AccountInfo) => account.accountId !== selectedAccount.accountId
    )[0]?.accountId;
  }, [getAccountSwitch?.data, selectedAccount.accountId]);
  const mutationDeleteUser = useStatusMutation(deleteUser, {
    onSuccess: () => {
      if (isDeactivateMode) {
        logout();
      }
      if (!isProfile) {
        notifySuccess(lang.label.user.notify.delete.success);
      }
      if (user.userId !== userId) {
        onSuccessDelete?.();
      } else if (user.userId === userId && switchAccount) {
        navigateSwitchAccount({ accountId: switchAccount }, false);
      }
    },
    onError: (err: CustomError) => {
      console.log(err);
      handleErrorNotify(err, lang.label.user.notify.delete.fail);
    },
  });

  const mutationDeactiveProfile = useStatusMutation(deactiveProfile, {
    onSuccess: () => {
      logout();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.deactivate.fail);
    },
  });

  const isDeactivateMode = useMemo(() => {
    if (isProfile) {
      return true;
    }
    if (user.userId === userId) {
      return getAccountSwitch?.data?.result?.length === 1;
    }
    return false;
  }, [isProfile, user.userId, userId, getAccountSwitch]);

  const onClickDelete = useCallback(() => {
    setIsDeleteConfirm(false);
    if (isProfile || (user.userId === userId && isDeactivateMode)) {
      mutationDeactiveProfile.mutate({
        userId,
      });
      return;
    }

    mutationDeleteUser.mutate({
      accountId: selectedAccount.accountId,
      userId,
    });
  }, [
    isDeactivateMode,
    isProfile,
    mutationDeactiveProfile,
    mutationDeleteUser,
    selectedAccount.accountId,
    user.userId,
    userId,
  ]);
  return (
    <Deactivate
      onDeactive={onClickDelete}
      title={
        isProfile
          ? "Are you sure you want to deactivate your profile?"
          : user.userId === userId && isDeactivateMode
          ? "Are you sure you want to deactivate yourself?"
          : "Are you sure you want to remove this user?"
      }
      subTitle={
        isProfile
          ? "This will remove you from every organization you are associated with."
          : user.userId === userId && isDeactivateMode
          ? "This will remove you from this organization you are associated with."
          : email
      }
      buttonLabel={isProfile ? "Deactivate" : "Remove from Organization"}
      disabled={
        (!isProfile && !deletable?.data?.result) ||
        isDeleteConfirm ||
        !mutationDeleteUser.idle ||
        !mutationDeactiveProfile.idle
      }
    />
  );
}
